import { Component, OnInit, DestroyRef, inject } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ConcursosAtivos } from 'src/app/models/concursos-ativos'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { ConcursoService } from 'src/app/services/concurso.service'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { PaisSensedia } from 'src/app/models/pais-sensedia'
import { ExternalAPIService } from 'src/app/services/external-api.service'
import { ExaminadorExternoConsulta } from 'src/app/models/examinador-externo-consulta.model'
import { forkJoin, take, catchError } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'inscricoes-nome-do-componente',
  templateUrl: './cadastrar-examinador-externo.component.html',
  styleUrls: ['./cadastrar-examinador-externo.component.scss'],
})
export class CadastrarExaminadorExternoComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  formConsultaExaminadorExterno!: FormGroup
  formCadastroExaminadorExterno!: FormGroup
  formAceiteExaminadorExterno!: FormGroup
  concursosAtivos!: ConcursosAtivos[]
  concursoFixo = false
  isBrasileiro: boolean = true
  paises!: PaisSensedia[]
  examinadorExternoConsulta!: ExaminadorExternoConsulta
  existingExaminador: boolean = false
  hasSearched = false

  constructor(
    private readonly fb: FormBuilder,
    private readonly comissaoJulgadoraService: ComissaoJulgadoraService,
    private readonly concursoService: ConcursoService,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly externalApiService: ExternalAPIService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.externalApiService.obterPaises().pipe(
      catchError(() => this.externalApiService.obterPaisesV2())
    , takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.paises = res
      },
      error: err => {
        this.unespCoreMessageService.showMessageWarning('Não foi possível obter a lista de países, volte mais tarde.')
      },
    })

    this.formCadastroExaminadorExterno = this.fb.group({
      nome: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      cpf: [''],
      rg: [''],
      passaporte: [''],
      rne: [''],
      tipoDocEstrangeiro: ['', [this.tipoDocEstrangeiroValidator()]],
      numeroDocEstrangeiro: ['', [this.numeroDocEstrangeiroValidator()]],
      dataNascimento: ['', Validators.required],
      instituicao: ['', Validators.required],
      siglaPais: [{ sigla: 'BR', pais: 'Brasil' }, Validators.required],
      idiomaComunicacao: ['', [Validators.required, Validators.pattern(/^(pt|en)$/)]],
    })

    this.formAceiteExaminadorExterno = this.fb.group({
      concurso: ['', Validators.required],
      papel: ['', Validators.required],
      deAcordo: ['', Validators.requiredTrue],
    })

    this.formConsultaExaminadorExterno = this.fb.group({
      tipoDocumento: ['cpf', Validators.required],
      cpf: [''],
      passaporte: [''],
    })

    this.formConsultaExaminadorExterno.get('tipoDocumento')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tipo: string) => {
      const cpfControl = this.formConsultaExaminadorExterno.get('cpf')
      const passaporteControl = this.formConsultaExaminadorExterno.get('passaporte')

      if (tipo === 'cpf') {
        cpfControl?.setValidators([Validators.required, this.cpfValidator()])
        passaporteControl?.clearValidators()
      } else {
        passaporteControl?.setValidators([Validators.required])
        cpfControl?.clearValidators()
      }

      cpfControl?.updateValueAndValidity()
      passaporteControl?.updateValueAndValidity()
    })

    // Set initial validators
    this.formConsultaExaminadorExterno.get('cpf')?.setValidators([Validators.required, this.cpfValidator()])

    this.formCadastroExaminadorExterno
      .get('tipoDocEstrangeiro')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tipoDocEstrangeiro: string) => {
        const numeroDocEstrangeiro = this.formCadastroExaminadorExterno.get('numeroDocEstrangeiro')
        if (tipoDocEstrangeiro) {
          numeroDocEstrangeiro?.setValidators([Validators.required])
        } else {
          numeroDocEstrangeiro?.clearValidators()
        }

        numeroDocEstrangeiro?.updateValueAndValidity({ emitEvent: false })
      })

    this.formCadastroExaminadorExterno
      .get('numeroDocEstrangeiro')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((numeroDocEstrangeiro: string) => {
        const tipoDocEstrangeiro = this.formCadastroExaminadorExterno.get('tipoDocEstrangeiro')
        if (numeroDocEstrangeiro) {
          tipoDocEstrangeiro?.setValidators([Validators.required])
        } else {
          tipoDocEstrangeiro?.clearValidators()
        }

        tipoDocEstrangeiro?.updateValueAndValidity({ emitEvent: false })
      })

    this.formCadastroExaminadorExterno.get('siglaPais')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pais: PaisSensedia) => {
      const cpf = this.formCadastroExaminadorExterno.get('cpf')

      if (pais?.sigla === 'BR') {
        this.isBrasileiro = true
        cpf?.setValidators([Validators.required])
        this.formCadastroExaminadorExterno.setValidators(null)
      } else {
        cpf?.clearValidators()
        this.isBrasileiro = false
      }

      // Atualiza status de validação dos campos
      cpf?.updateValueAndValidity()
      this.formCadastroExaminadorExterno.updateValueAndValidity()
    })

    forkJoin({
      params: this.route.queryParams.pipe(take(1)),
      concursos: this.comissaoJulgadoraService.buscarConcursosAtivos(),
    }).subscribe(({ params, concursos }) => {
      this.concursosAtivos = concursos

      const concursoId = params['concurso'] || this.findParentParam('id')
      if (concursoId) {
        let concurso = this.concursosAtivos.find(c => c.id === Number(concursoId))
        if (concurso) {
          this.setConcursoFixo(concurso)
        } else {
          this.concursoService.get(Number(concursoId)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(c => {
            const entry = { id: c.id as number, titulo: c.titulo }
            this.concursosAtivos = [entry, ...this.concursosAtivos]
            this.setConcursoFixo(entry)
          })
        }
      }
    })
  }

  private findParentParam(param: string): string | null {
    let r = this.route.parent
    while (r) {
      const val = r.snapshot?.params?.[param]
      if (val) return val
      r = r.parent
    }
    return null
  }

  private setConcursoFixo(concurso: { id: number; titulo: string }): void {
    this.formAceiteExaminadorExterno.patchValue({ concurso })
    if (this.findParentParam('id')) {
      this.concursoFixo = true
      this.formAceiteExaminadorExterno.get('concurso')?.disable()
    }
  }

  onSubmit(): void {
    if (this.formCadastroExaminadorExterno.valid && this.formAceiteExaminadorExterno.valid) {
      let correctCpf = ''
      let correctPassport = ''

      if (this.formConsultaExaminadorExterno.get('tipoDocumento')?.value === 'cpf') {
        correctCpf = this.formConsultaExaminadorExterno.get('cpf')?.value
        correctPassport = this.formCadastroExaminadorExterno.get('passaporte')?.value
      } else {
        correctCpf = this.formCadastroExaminadorExterno.get('cpf')?.value
        correctPassport = this.formConsultaExaminadorExterno.get('passaporte')?.value
      }


      const concursoValue = this.formAceiteExaminadorExterno.getRawValue().concurso
      const concursoId = concursoValue?.id || concursoValue
      this.comissaoJulgadoraService
        .cadastrarExaminadorExterno({
          ...this.formCadastroExaminadorExterno.value,
          ...this.formAceiteExaminadorExterno.value,
          concurso: concursoId,
          cpf: correctCpf,
          passaporte: correctPassport,
          siglaPais: this.formCadastroExaminadorExterno.get('siglaPais')?.value.sigla.toUpperCase(),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: resp => {
            this.unespCoreMessageService.showMessageSuccess(
              'Solicitação de cadastro de examinador externo realizada com sucesso!'
            )
          },
          error: err => {
            this.unespCoreMessageService.showMessageError(
              'Ocorreu um erro ao cadastrar o examinador: ' + err.error.message
            )
          },
        })
    }
  }

  queryExaminadorExterno(): void {
    if (this.formConsultaExaminadorExterno.valid) {
      let searchType = 'cpf'
      let searchValue = ''

      let formValue = this.formConsultaExaminadorExterno.value

      if (formValue.tipoDocumento === 'cpf') {
        searchValue = formValue.cpf
      } else {
        searchType = 'passaporte'
        searchValue = formValue.passaporte
      }

      this.comissaoJulgadoraService.consultarExaminadorExterno(searchType, searchValue).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: examinadorExternoConsulta => {
          this.examinadorExternoConsulta = examinadorExternoConsulta

          // Search paises values
          let paisObject = this.paises.find(p => p.sigla === examinadorExternoConsulta.siglaPais)

          this.formCadastroExaminadorExterno.patchValue({
            nome: examinadorExternoConsulta.nome,
            email: examinadorExternoConsulta.email,
            rg: examinadorExternoConsulta.rg,
            passaporte: examinadorExternoConsulta.passaporte,
            cpf: examinadorExternoConsulta.cpf,
            rne: examinadorExternoConsulta.rne,
            tipoDocEstrangeiro: examinadorExternoConsulta.tipoDocEstrangeiro,
            numeroDocEstrangeiro: examinadorExternoConsulta.docEstrangeiro,
            dataNascimento: examinadorExternoConsulta.dataNascimento,
            instituicao: examinadorExternoConsulta.instituicao,
            siglaPais: paisObject,
            idiomaComunicacao: examinadorExternoConsulta.idiomaComunicacao,
          })

          const tipoDoc = this.formConsultaExaminadorExterno.get('tipoDocumento')?.value
          this.formConsultaExaminadorExterno.get(tipoDoc)?.disable()
          this.existingExaminador = true
          this.hasSearched = true
        },
        error: err => {
          // I know this is not good ok, but I'm out of time, and you are out of touch
          if (err.status === 404 && err.error.message === 'Examinador não encontrado') {
            const tipoDoc = this.formConsultaExaminadorExterno.get('tipoDocumento')?.value
            this.formConsultaExaminadorExterno.get(tipoDoc)?.disable()
            this.hasSearched = true
          } else {
            this.unespCoreMessageService.showMessageError('Ocorreu um erro: ' + err.message)
          }
        },
      })
    }
  }

  cpfValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const cpf = control.value
      if (!cpf) {
        return null // Let required validator handle empty values
      }

      // Check if CPF contains letters
      if (/[a-zA-Z]/.test(cpf)) {
        return { cpfContainsLetters: true }
      }

      if (!this.isCPFValid(cpf)) {
        return { cpfInvalido: true }
      }

      return null
    }
  }

  tipoDocEstrangeiroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const valor = control.value?.toString().toUpperCase()
      const proibidos = ['CPF', 'RG', 'RNE', 'PASSAPORTE']

      if (proibidos.includes(valor)) {
        return { tipoDocInvalido: true }
      }

      return null
    }
  }

  numeroDocEstrangeiroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control?.parent
      if (!form) {
        return null
      }

      const cpf = form.get('cpf')?.value
      const rg = form.get('rg')?.value
      const passaporte = form.get('passaporte')?.value
      const rne = form.get('rne')?.value

      const numeroDocEstrangeiro = control.value

      if (!numeroDocEstrangeiro) {
        return null
      }

      if (
        numeroDocEstrangeiro === cpf ||
        numeroDocEstrangeiro === rg ||
        numeroDocEstrangeiro === passaporte ||
        numeroDocEstrangeiro === rne
      ) {
        return { numeroDocEstrangeiroDuplicado: true }
      }

      return null
    }
  }

  validaCPF(): FormGroup {
    const cpf = this.formCadastroExaminadorExterno.get('cpf')?.value
    if (!this.isCPFValid(cpf)) {
      this.formCadastroExaminadorExterno.get('cpf')?.setErrors({ incorrect: true })
    }
    return this.formCadastroExaminadorExterno
  }

  isCPFValid(value: string): boolean {
    const cpf = value.replace(/[^\d]+/g, '')
    if (cpf.length == 0) return true
    if (
      cpf.length != 11 ||
      cpf == '00000000000' ||
      cpf == '11111111111' ||
      cpf == '22222222222' ||
      cpf == '33333333333' ||
      cpf == '44444444444' ||
      cpf == '55555555555' ||
      cpf == '66666666666' ||
      cpf == '77777777777' ||
      cpf == '88888888888' ||
      cpf == '99999999999'
    )
      return false
    // Valida 1o digito
    let add = 0
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i)
    let rev = 11 - (add % 11)
    if (rev == 10 || rev == 11) rev = 0
    if (rev != parseInt(cpf.charAt(9))) return false
    // Valida 2o digito
    add = 0
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i)
    rev = 11 - (add % 11)
    if (rev == 10 || rev == 11) rev = 0
    if (rev != parseInt(cpf.charAt(10))) return false
    return true
  }

  navegarListaExaminadores(): void {
    const concursoValue = this.formAceiteExaminadorExterno.get('concurso')?.value
    const concursoId = concursoValue?.id || concursoValue || this.findParentParam('id')
    if (concursoId) {
      this.router.navigate(['/concurso', concursoId, 'examinadores'])
    }
  }
}
